import type { HistoryEntry, Moved, PublicAnswer, PublicState, Speed, TurnStep } from "../protocol";
import { isPregameAnswer, isPregameDecision, sameStep, stepOf } from "./game-text";

// One StateUpdate as the board shows it, with the whole History up to it. `walking` is a step the
// Room passed through on the way to the next update, shown on the board as it was before that
// update (stepsPassed); null for an update as the Room sent it.
export type ShownUpdate = { seat: string | null; state: PublicState; history: HistoryEntry[]; moves: Moved[]; walking: TurnStep | null };

// How long an AI seat's action stays on the board before the update after it, at Normal: the Room's
// shown_after_action_ms (server/room). The room's AI speed scales it, and Instant shows each at once.
const BEAT_MS = 1000;
// And each step the Room passed through on the way.
const STEP_BEAT_MS = 300;
// And the first update of a new turn, which the board announces ("Medium AI's turn").
const TURN_BEAT_MS = 1400;
const BEAT_SCALE: Record<Speed["kind"], number> = { Half: 2, Normal: 1, Double: 0.5, Quadruple: 0.25, Instant: 0 };

export function beatMs(speed: Speed["kind"]): number {
  return BEAT_MS * BEAT_SCALE[speed];
}

export function stepBeatMs(speed: Speed["kind"]): number {
  return STEP_BEAT_MS * BEAT_SCALE[speed];
}

export function turnBeatMs(speed: Speed["kind"]): number {
  return TURN_BEAT_MS * BEAT_SCALE[speed];
}

// The turn and step an update shows: the step it walks through, else its state's.
export function shownStepOf(update: ShownUpdate): TurnStep {
  return update.walking ?? stepOf(update.state);
}

// `update` shows a later turn than the update before it.
export function startsTurn(update: ShownUpdate, before: ShownUpdate): boolean {
  return !isPregameDecision(before.state.decision) && shownStepOf(update).turn !== shownStepOf(before).turn;
}

// Each step the History passed through between two updates, but the ones they're at: the board
// before, walking through it with the History up to its end. None from the pregame's board, which
// has no step to walk on from.
export function stepsPassed(before: ShownUpdate, after: ShownUpdate): ShownUpdate[] {
  if (isPregameDecision(before.state.decision)) return [];
  const ends: { step: TurnStep; end: number }[] = [];
  after.history.forEach((e, i) => {
    if (i < before.history.length || isPregameAnswer(e.answer)) return;
    const last = ends.at(-1);
    if (last && sameStep(last.step, e.step)) last.end = i + 1;
    else ends.push({ step: e.step, end: i + 1 });
  });
  if (ends.length > 0 && sameStep(ends[0]!.step, stepOf(before.state))) ends.shift();
  if (ends.length > 0 && sameStep(ends.at(-1)!.step, stepOf(after.state))) ends.pop();
  return ends.map(({ step, end }) => ({ ...before, history: after.history.slice(0, end), moves: [], walking: step }));
}

// What the Room sends a StateUpdate after when it plays an AI seat's answers at once (server/room's
// is_action): a land, a spell, an ability, an attack or a block.
export function isAction(answer: PublicAnswer): boolean {
  switch (answer.kind) {
    case "Take": {
      const kind = answer.values[0].kind;
      return kind !== "PassPriority" && kind !== "TapForMana" && kind !== "UntapForMana" && kind !== "Concede";
    }
    case "Attackers": return answer.values[0].length > 0;
    case "Blockers": return answer.values[0].length > 0;
    default: return false;
  }
}

// The StateUpdates the board has yet to show, oldest first. The Room plays an AI seat's turn at
// once and sends the board as each of its actions left it, then where it stopped; each update that
// shows another action stays a beat before the next, so the turn plays out in order.
export class PacedUpdates {
  private waiting: ShownUpdate[] = [];
  private shown: ShownUpdate | null = null;
  private beat: number | null = null;

  constructor(
    private readonly show: (update: ShownUpdate) => void,
    // How long `update` stays before the next, given the update shown before it.
    private readonly beatAfter: (update: ShownUpdate, before: ShownUpdate | null) => number,
  ) {}

  // A newer update waits behind the one shown.
  get behind(): boolean {
    return this.waiting.length > 0;
  }

  receive(update: ShownUpdate): void {
    this.waiting.push(update);
    if (this.beat === null) this.showNext();
  }

  // The latest update now, and the next to come as soon as it does.
  showLatest(): void {
    this.endBeat();
    const latest = this.waiting.at(-1);
    this.waiting = [];
    if (latest) this.showNow(latest);
  }

  // The next update to come is shown as soon as it does.
  endBeat(): void {
    if (this.beat !== null) window.clearTimeout(this.beat);
    this.beat = null;
  }

  // Forgets every update, shown or waiting: the next starts afresh (a WholeHistory, or a new game).
  forget(): void {
    this.endBeat();
    this.waiting = [];
    this.shown = null;
  }

  private showNext(): void {
    const next = this.waiting.shift();
    if (!next) return;
    const before = this.shown;
    this.showNow(next);
    const ms = this.beatAfter(next, before);
    if (ms > 0) this.beat = window.setTimeout(() => { this.beat = null; this.showNext(); }, ms);
    else this.showNext();
  }

  private showNow(update: ShownUpdate): void {
    this.shown = update;
    this.show(update);
  }
}
